import React from "react";
import {
  Box,
  Modal,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Divider,
} from "@mui/material";
import { IoCloseCircleOutline } from "react-icons/io5";

interface Medicine {
  sNo: number;
  name: string;
  strength: string;
  dosage: string;
  intake: string;
  duration: string;
}

interface PrescriptionData {
  consultantName: string;
  dateTime: string;
  pulseRate: number;
  bloodPressure: string;
  complaints: string;
  tests: string[];
  hne: string;
  medicines: Medicine[];
  advice: string;
}

interface PrescriptionPopupProps {
  open: boolean;
  handleClose: () => void;
  data: PrescriptionData;
}

const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 600,
  bgcolor: "background.paper",
  borderRadius: 1,
  boxShadow: 24,
  p: 4,
  overflowY: "auto",
  maxHeight: "80vh",
};

const PrescriptionPopup: React.FC<PrescriptionPopupProps> = ({
  open,
  handleClose,
  data,
}) => {
  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
    >
      <Box sx={style}>
        {/* Close button */}
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{ position: "absolute", top: 8, right: 8, color: "grey.500" }}
        >
          <IoCloseCircleOutline />
        </IconButton>

        <Typography id="modal-title" variant="h6" component="h2" sx={{ mb: 1 }}>
          Prescription by {data.consultantName || "[Consultant name]"}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {data.dateTime}
        </Typography>

        <Divider sx={{ mb: 2 }} />

        {/* Vitals */}
        <div className="row mt-3">
          <span className="prescription-headline col-12 rounded">Vitals</span>
          <Box mt={2}>
            <Box display="flex">
              <Typography
                variant="subtitle2"
                style={{
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  marginRight: "20px",
                }}
              >
                Pulse Rate (Bpm)
              </Typography>
              <Typography
                variant="subtitle2"
                style={{
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  marginRight: "20px",
                }}
              >
                Blood Pressure (mmHg)
              </Typography>
            </Box>

            {/* Content Row */}
            <Box display="flex" mt={1}>
              <Typography
                variant="body2"
                style={{ fontSize: "0.875rem", marginRight: "20px" }}
              >
                88 bpm
              </Typography>
              <Typography
                variant="body2"
                color="textSecondary"
                style={{ fontSize: "0.875rem", marginLeft: "80px" }}
              >
                122/80
              </Typography>
            </Box>
          </Box>
        </div>

        {/* Complaints */}
        <div className="row mt-3">
          <span className="prescription-headline col-12 rounded">
            Complaints
          </span>
          <p className="document-description mt-3">
            Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do
            eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim
            ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut
            aliquip ex ea commodo consequat.
          </p>
        </div>

        {/* Tests */}
        <div className="row mt-3">
          <span className="prescription-headline col-12 rounded">Tests</span>
          <Box mt={2}>
            <Box display="flex">
              <Typography
                variant="subtitle2"
                style={{
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  marginRight: "20px",
                }}
              >
                Test Name
              </Typography>
            </Box>

            {/* Content Row */}
            <Box display="flex" mt={1}>
              <Typography
                variant="body2"
                style={{ fontSize: "0.875rem", marginRight: "20px" }}
                className="prescription-headline col-3 rounded"
              >
                Hemoglobin Test
              </Typography>
              <Typography
                variant="body2"
                style={{ fontSize: "0.875rem", marginLeft: "20px" }}
                className="prescription-headline col-3 rounded"
              >
                Hemoglobin Test
              </Typography>
            </Box>
          </Box>
        </div>

        <div className="row mt-3">
          <span className="prescription-headline col-12 rounded">H & E</span>
          <p className="document-description mt-3">
            Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do
            eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim
            ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut
            aliquip ex ea commodo consequat.
          </p>
        </div>

        <Divider sx={{ my: 2 }} />

        <Typography
          variant="subtitle2"
          gutterBottom
          className="prescription-headline col-12 rounded"
        >
          Medicine
        </Typography>
        <Table size="small" sx={{ mt: 1 }}>
          <TableHead>
            <TableRow>
              <TableCell align="center">S.No</TableCell>
              <TableCell align="center">Medicine</TableCell>
              <TableCell align="center">Strength</TableCell>
              <TableCell align="center">Dosage</TableCell>
              <TableCell align="center">Intake</TableCell>
              <TableCell align="center">Duration</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.medicines.map((medicine) => (
              <TableRow key={medicine.sNo}>
                <TableCell align="center">{medicine.sNo}</TableCell>
                <TableCell align="center">{medicine.name}</TableCell>
                <TableCell align="center">{medicine.strength}</TableCell>
                <TableCell align="center">{medicine.dosage}</TableCell>
                <TableCell align="center">{medicine.intake}</TableCell>
                <TableCell align="center">{medicine.duration}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Divider sx={{ my: 2 }} />

        {/* Advice */}
        <div className="row mt-3">
          <span className="prescription-headline col-12 rounded">Advice</span>
          <p className="document-description mt-3">
            Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do
            eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim
            ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut
            aliquip ex ea commodo consequat.
          </p>
        </div>
      </Box>
    </Modal>
  );
};

export default PrescriptionPopup;
